import { NAKSHATRAS_27, PLANETS, SIGNS } from './constants.js';

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function normalize360(value) {
  const result = Number(value) % 360;
  return result < 0 ? result + 360 : result;
}

export function shortestAngularDistance(a, b) {
  const delta = Math.abs(normalize360(a) - normalize360(b));
  return Math.min(delta, 360 - delta);
}

export function signInfo(longitude) {
  const normalized = normalize360(longitude);
  const signIndex = Math.floor(normalized / 30);
  return {
    signIndex,
    sign: SIGNS[signIndex].name,
    signShort: SIGNS[signIndex].short,
    signGlyph: SIGNS[signIndex].glyph,
    degreeInSign: normalized % 30,
  };
}

export function nakshatraInfo(longitude) {
  const normalized = normalize360(longitude);
  const span = 360 / 27;
  const padaSpan = span / 4;
  const nakshatraIndex = Math.floor(normalized / span) % 27;
  const offset = normalized - nakshatraIndex * span;
  return {
    nakshatraIndex,
    nakshatra: NAKSHATRAS_27[nakshatraIndex],
    pada: Math.min(4, Math.floor(offset / padaSpan) + 1),
    degreeInNakshatra: offset,
  };
}

export function enrichPlanet(position) {
  if (!position || !Number.isFinite(Number(position.longitude))) {
    throw new TypeError('Planet position must include a finite longitude.');
  }

  const longitude = normalize360(Number(position.longitude));
  const planetId = String(position.id || position.planet || '').toLowerCase();
  const meta = PLANETS[planetId] || {
    id: planetId || 'unknown',
    name: position.name || planetId || 'Unknown',
    short: position.short || String(position.name || '?').slice(0, 2),
    className: 'planet-generic',
  };
  const speed = Number(position.speed ?? position.longitudeSpeed ?? 0);

  return {
    ...position,
    ...meta,
    longitude,
    speed,
    retrograde: position.retrograde ?? speed < 0,
    ...signInfo(longitude),
    ...nakshatraInfo(longitude),
  };
}

export function enrichPlanetList(list = []) {
  return list.map(enrichPlanet);
}

export function polarToCartesian(cx, cy, radius, angleDegrees) {
  const angleRadians = ((angleDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRadians),
    y: cy + radius * Math.sin(angleRadians),
  };
}

export function zodiacAngle(longitude, ascendant = 0) {
  // Places the Ascendant at 9 o'clock and runs zodiacal order counter-clockwise.
  return normalize360(270 - normalize360(longitude - ascendant));
}

export function svgElement(tagName, attributes = {}, text = null) {
  const element = document.createElementNS(SVG_NS, tagName);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'className') {
      element.setAttribute('class', String(value));
    } else {
      element.setAttribute(key, String(value));
    }
  }
  if (text !== null && text !== undefined) element.textContent = String(text);
  return element;
}

export function htmlElement(tagName, attributes = {}, children = []) {
  const element = document.createElement(tagName);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'className') {
      element.className = String(value);
    } else if (key === 'text') {
      element.textContent = String(value);
    } else if (key === 'dataset' && typeof value === 'object') {
      Object.assign(element.dataset, value);
    } else if (key in element && key !== 'style') {
      try {
        element[key] = value;
      } catch {
        element.setAttribute(key, String(value));
      }
    } else {
      element.setAttribute(key, String(value));
    }
  }
  const childArray = Array.isArray(children) ? children : [children];
  for (const child of childArray) {
    if (child === null || child === undefined || child === false) continue;
    element.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return element;
}

export function clearElement(element) {
  while (element.firstChild) element.removeChild(element.firstChild);
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function formatDegrees(value, { includeSeconds = false } = {}) {
  const normalized = normalize360(Number(value));
  if (includeSeconds) {
    const totalSeconds = Math.round(normalized * 3600) % (360 * 3600);
    const degrees = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${degrees}°${String(minutes).padStart(2, '0')}′${String(seconds).padStart(2, '0')}″`;
  }
  const totalMinutes = Math.round(normalized * 60) % (360 * 60);
  const degrees = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${degrees}°${String(minutes).padStart(2, '0')}′`;
}

export function formatDegreeInSign(value) {
  const normalized = ((Number(value) % 30) + 30) % 30;
  const totalMinutes = Math.min(1799, Math.round(normalized * 60));
  const degrees = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(degrees).padStart(2, '0')}°${String(minutes).padStart(2, '0')}′`;
}

export function formatDateTimeForZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`;
}

export function dateTimeLocalToNaiveDate(value) {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/);
  if (!match) throw new Error('Date/time must be YYYY-MM-DDTHH:mm[:ss[.SSS]].');
  const [, year, month, day, hour, minute, second = '0', millisecond = '0'] = match;
  // UTC is deliberately used as a wall-clock arithmetic container. The server
  // resolves the wall-clock value inside the selected IANA timezone.
  return new Date(Date.UTC(
    +year,
    +month - 1,
    +day,
    +hour,
    +minute,
    +second,
    Number(millisecond.padEnd(3, '0')),
  ));
}

export function naiveDateToDateTimeLocal(date) {
  const pad = (number) => String(number).padStart(2, '0');
  return [
    date.getUTCFullYear(),
    '-',
    pad(date.getUTCMonth() + 1),
    '-',
    pad(date.getUTCDate()),
    'T',
    pad(date.getUTCHours()),
    ':',
    pad(date.getUTCMinutes()),
    ':',
    pad(date.getUTCSeconds()),
  ].join('');
}

export function addWallClockStep(value, unit, amount) {
  const date = dateTimeLocalToNaiveDate(value);
  switch (unit) {
    case 'years':
      date.setUTCFullYear(date.getUTCFullYear() + amount);
      break;
    case 'months':
      date.setUTCMonth(date.getUTCMonth() + amount);
      break;
    case 'days':
      date.setUTCDate(date.getUTCDate() + amount);
      break;
    case 'hours':
      date.setUTCHours(date.getUTCHours() + amount);
      break;
    case 'minutes':
      date.setUTCMinutes(date.getUTCMinutes() + amount);
      break;
    case 'seconds':
      date.setUTCSeconds(date.getUTCSeconds() + amount);
      break;
    default:
      throw new Error(`Unsupported step unit: ${unit}`);
  }
  return naiveDateToDateTimeLocal(date);
}

export function createTitle(text) {
  return svgElement('title', {}, text);
}

export function groupBy(items, keyFn) {
  const map = new Map();
  for (const item of items) {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return map;
}

export function escapeCsv(value) {
  const string = String(value ?? '');
  if (!/[",\n]/.test(string)) return string;
  return `"${string.replaceAll('"', '""')}"`;
}

export function downloadTextFile(filename, content, type = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const SVG_PRESENTATION_PROPERTIES = [
  'color',
  'display',
  'dominant-baseline',
  'fill',
  'fill-opacity',
  'fill-rule',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'letter-spacing',
  'opacity',
  'paint-order',
  'shape-rendering',
  'stop-color',
  'stop-opacity',
  'stroke',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'stroke-opacity',
  'stroke-width',
  'text-anchor',
  'text-rendering',
  'vector-effect',
  'visibility',
];

function inlineSvgComputedStyles(source, clone) {
  const view = source?.ownerDocument?.defaultView;
  if (!view?.getComputedStyle) return clone;

  const sourceElements = [source, ...source.querySelectorAll('*')];
  const clonedElements = [clone, ...clone.querySelectorAll('*')];
  sourceElements.forEach((element, index) => {
    const clonedElement = clonedElements[index];
    if (!clonedElement?.style) return;
    const computed = view.getComputedStyle(element);
    SVG_PRESENTATION_PROPERTIES.forEach((property) => {
      const value = computed.getPropertyValue(property);
      if (value) clonedElement.style.setProperty(property, value);
    });
  });
  return clone;
}

export function serializeSvg(svg, { footerText = '' } = {}) {
  const clone = svg.cloneNode(true);
  clone.setAttribute('xmlns', SVG_NS);
  inlineSvgComputedStyles(svg, clone);
  if (footerText) appendSvgFooter(clone, footerText);
  return new XMLSerializer().serializeToString(clone);
}

export function downloadSvg(svg, filename, options = {}) {
  downloadTextFile(filename, serializeSvg(svg, options), 'image/svg+xml;charset=utf-8');
}

export function appendSvgFooter(svg, text) {
  if (!svg || typeof svg.getAttribute !== 'function') {
    throw new TypeError('svg must be an SVG element.');
  }

  const viewBox = parseSvgViewBox(svg);
  const lines = wrapSvgFooterText(String(text), viewBox.width);
  const lineHeight = Math.max(15, Math.min(20, viewBox.width / 55));
  const footerPadding = 14;
  const footerHeight = footerPadding * 2 + lines.length * lineHeight;
  const footerTop = viewBox.minY + viewBox.height;

  svg.setAttribute('viewBox', [
    viewBox.minX,
    viewBox.minY,
    viewBox.width,
    viewBox.height + footerHeight,
  ].join(' '));

  const explicitHeight = Number(svg.getAttribute('height'));
  if (Number.isFinite(explicitHeight) && explicitHeight > 0) {
    svg.setAttribute('height', String(explicitHeight + footerHeight));
  }

  const group = svgElement('g', {
    className: 'aat-svg-disclaimer-footer',
    'data-disclaimer-footer': 'true',
  });
  group.append(svgElement('rect', {
    x: viewBox.minX,
    y: footerTop,
    width: viewBox.width,
    height: footerHeight,
    fill: '#ffffff',
    stroke: '#d8d8d8',
    'stroke-width': '1',
  }));

  const textElement = svgElement('text', {
    x: viewBox.minX + viewBox.width / 2,
    y: footerTop + footerPadding + lineHeight * 0.8,
    fill: '#4b5563',
    'font-family': 'Arial, Helvetica, sans-serif',
    'font-size': String(Math.max(10, Math.min(13, viewBox.width / 70))),
    'text-anchor': 'middle',
    role: 'note',
  });
  lines.forEach((line, index) => {
    textElement.append(svgElement('tspan', {
      x: viewBox.minX + viewBox.width / 2,
      dy: index === 0 ? '0' : String(lineHeight),
    }, line));
  });
  group.append(textElement);
  svg.append(group);
  return svg;
}

function parseSvgViewBox(svg) {
  const raw = String(svg.getAttribute('viewBox') || '').trim();
  const values = raw.split(/[\s,]+/).map(Number);
  if (values.length === 4 && values.every(Number.isFinite) && values[2] > 0 && values[3] > 0) {
    return { minX: values[0], minY: values[1], width: values[2], height: values[3] };
  }
  const width = Number(svg.getAttribute('width')) || 900;
  const height = Number(svg.getAttribute('height')) || 900;
  return { minX: 0, minY: 0, width, height };
}

function wrapSvgFooterText(text, width) {
  const maxCharacters = Math.max(38, Math.floor(width / 7));
  const logicalLines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const wrapped = [];

  for (const logicalLine of logicalLines) {
    const words = logicalLine.split(/\s+/);
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > maxCharacters && current) {
        wrapped.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) wrapped.push(current);
  }
  return wrapped.length ? wrapped : [''];
}

export function safeText(value, fallback = '—') {
  return value === null || value === undefined || value === '' ? fallback : String(value);
}
